import React from "react";
import { Button, Divider } from "@material-ui/core";
import { Formik, Form, Field } from "formik";
import { TextField } from "formik-material-ui";
import * as Yup from "yup";
import clsx from "clsx";
import MobilePaymentService from "../../../services/MobilePaymentService";
import apiClient from "../../../auth/apiClient";
import { useEnqueueSnackbar } from "../../../hooks/useEnqueueSnackbar";
import {
  emailValidator,
  mobileValidator,
  isValidEmail,
  isValidMobile,
} from "./receiptValidation";
import { useSendReceiptStyles } from "./Receipts.styles";

const mobilePayService = new MobilePaymentService(apiClient);

/**
 * Yup schema for the redesigned Send Receipt form. Composes the pure,
 * requirement-driven validators from receiptValidation.js (task 10.2) so the
 * email and phone fields share the exact validation rules exercised by the
 * property tests. Each field is validated independently when its own submit
 * affordance is used.
 */
export const sendReceiptSchema = Yup.object().shape({
  email: emailValidator,
  phone: mobileValidator,
});

/**
 * Redesigned Send Receipt form.
 *
 * A single Formik form presenting an email field and a phone field, each with
 * its own submit affordance. Behavior per requirements 9.1, 9.3, 9.4, 9.5, 9.6:
 *  - On a successful send, an accepted confirmation is shown.
 *  - On validation failure, a field-level error is shown and the entered value
 *    is retained without sending.
 *  - On send failure, a send-failed error is shown and the entered value is
 *    retained for retry.
 *
 * Input is trimmed before sending. Email addresses and phone numbers are never
 * logged (PII), consistent with the security/logging standards.
 *
 * @param {object} props Component props.
 * @param {string} props.ticketId The ticket the receipt relates to.
 * @returns {JSX.Element} The rendered Send Receipt form.
 */
const SendReceiptForm = ({ ticketId }) => {
  const classes = useSendReceiptStyles();
  const enqueueSnackbar = useEnqueueSnackbar();

  /**
   * Sends the receipt via email. Validates the email locally first; on
   * validation failure a field-level error is surfaced and nothing is sent.
   *
   * @param {object} formik Formik bag (values + helpers) for the email field.
   */
  const sendEmailReceipt = async ({ values, setFieldTouched, setFieldError }) => {
    const email = (values.email || "").trim();

    setFieldTouched("email", true, false);
    if (!isValidEmail(email)) {
      setFieldError("email", "Invalid email address");
      return;
    }

    try {
      await mobilePayService.emailReceipt(ticketId, email);
      enqueueSnackbar("Receipt sent", { variant: "success", tag: "receiptEmailSent" });
    } catch {
      enqueueSnackbar("Failed to send email receipt", {
        variant: "error",
        tag: "receiptEmailFailed",
      });
    }
  };

  /**
   * Sends the receipt via SMS. Validates the phone locally first; on validation
   * failure a field-level error is surfaced and nothing is sent.
   *
   * @param {object} formik Formik bag (values + helpers) for the phone field.
   */
  const sendSmsReceipt = async ({ values, setFieldTouched, setFieldError }) => {
    const phone = (values.phone || "").trim();

    setFieldTouched("phone", true, false);
    if (!isValidMobile(phone)) {
      setFieldError("phone", "Mobile number is not valid");
      return;
    }

    try {
      await mobilePayService.sendSMSReceipt({ TicketID: ticketId, To: phone });
      enqueueSnackbar("Receipt sent", { variant: "success", tag: "receiptSmsSent" });
    } catch {
      enqueueSnackbar("Failed to send text receipt", {
        variant: "error",
        tag: "receiptSmsFailed",
      });
    }
  };

  return (
    <Formik
      initialValues={{ email: "", phone: "" }}
      validationSchema={sendReceiptSchema}
      validateOnMount={false}
      // Both affordances submit their own field explicitly, so no-op here.
      onSubmit={() => {}}
    >
      {({ values, setFieldTouched, setFieldError }) => (
        <Form className={clsx(classes.form, "send-receipt-form")}>
          <div className={clsx(classes.fieldGroup, "send-receipt-email-group")}>
            <Field
              component={TextField}
              className={clsx(classes.input, "receipt-email-field")}
              variant="outlined"
              name="email"
              data-id="receipt-email"
              label="Email Address"
            />
            <Button
              variant="contained"
              className={clsx(classes.button, "send-email-receipt-button")}
              color="primary"
              aria-label="send-email-receipt-button"
              title="Email Receipt"
              onClick={() =>
                sendEmailReceipt({ values, setFieldTouched, setFieldError })
              }
            >
              Send Receipt
            </Button>
          </div>

          <Divider className={classes.divider} />

          <div className={clsx(classes.fieldGroup, "send-receipt-phone-group")}>
            <Field
              component={TextField}
              className={clsx(classes.input, "receipt-phone-field")}
              variant="outlined"
              name="phone"
              data-id="receipt-phone"
              label="Phone Number"
            />
            <Button
              variant="contained"
              className={clsx(classes.button, "send-sms-receipt-button")}
              color="primary"
              aria-label="send-sms-receipt-button"
              title="Text Receipt"
              onClick={() =>
                sendSmsReceipt({ values, setFieldTouched, setFieldError })
              }
            >
              Send Receipt
            </Button>
          </div>
        </Form>
      )}
    </Formik>
  );
};

export default SendReceiptForm;
